import { ALL_PARAMETERS, ParameterKey } from './weldParameterAggregator'

/**
 * Persisted column layout for the redesigned Welding Parameters table.
 *
 * Per-parameter cells (Min/Avg/Max) are independently toggleable so the user
 * can show e.g. "Min only" for one parameter and "Min/Avg/Max" for another.
 * If all three flags for a parameter are false the whole column collapses out
 * of the table.
 *
 * The Torch column is a single boolean toggle (no aggregation sub-columns)
 * and lives under the Basic Info category in the column selector. It is OFF
 * by default — users can re-enable it from the column selector.
 *
 * Storage is intentionally `localStorage`-only — issue 06 calls it out as a
 * per-user-per-browser preference; we never sync it to the server.
 */
export type AggKey = 'min' | 'avg' | 'max'

export type ColumnVisibility = Record<AggKey, boolean>

export type VisibleColumns = Record<ParameterKey, ColumnVisibility> & {
  /** Whether the Torch column is rendered. Defaults to false. */
  torch: boolean
}

export const COLUMN_LAYOUT_STORAGE_KEY = 'wps:weldParams:visibleColumns:v1'

/** Issue 03's live columns, all three sub-cells ON. */
const DEFAULT_VISIBLE_PARAMS: ParameterKey[] = [
  'preheat',
  'volts',
  'amps',
  'wireFeed',
  'travelSpeed',
  'heatInput'
]

export function defaultVisibleColumns(): VisibleColumns {
  const out = { torch: false } as VisibleColumns
  const visible = new Set<ParameterKey>(DEFAULT_VISIBLE_PARAMS)
  for (const key of ALL_PARAMETERS) {
    const on = visible.has(key)
    out[key] = { min: on, avg: on, max: on }
  }
  return out
}

export function loadVisibleColumns(): VisibleColumns {
  const raw = readRaw()
  if (raw === null) return defaultVisibleColumns()

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return defaultVisibleColumns()
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return defaultVisibleColumns()
  }

  const merged = defaultVisibleColumns()
  const obj = parsed as Record<string, unknown>
  for (const key of ALL_PARAMETERS) {
    const entry = obj[key]
    if (isColumnVisibility(entry)) {
      merged[key] = entry
    }
  }
  if (typeof obj.torch === 'boolean') {
    merged.torch = obj.torch
  }
  return merged
}

export function saveVisibleColumns(value: VisibleColumns): void {
  try {
    localStorage.setItem(COLUMN_LAYOUT_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage may be unavailable (Safari private mode, quota); fail soft.
  }
}

function readRaw(): string | null {
  try {
    return localStorage.getItem(COLUMN_LAYOUT_STORAGE_KEY)
  } catch {
    return null
  }
}

function isColumnVisibility(value: unknown): value is ColumnVisibility {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.min === 'boolean' &&
    typeof v.avg === 'boolean' &&
    typeof v.max === 'boolean'
  )
}
